import React, { useEffect, useMemo, useState } from 'react'
import { Sidebar, Heading, Button, Text, Input, Tooltip, Select } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { supabase } from '../lib/supabase'
import { BRAND_COLOR } from '../constants'
import { PlusIcon, XMarkIcon, PencilIcon, TrashIcon, PowerIcon } from '@heroicons/react/24/outline'
import CalendarModal from '../components/agendamentos/CalendarModal'

type RetryStrategy = 'fixed' | 'exponential'
type RunStatus = 'success' | 'failed' | 'cancelled' | null

interface Process {
  id: string
  key: string
  name: string
  description?: string | null
  is_active: boolean
}

interface ProcessSchedule {
  id: string
  process_id: string
  description: string | null
  enabled: boolean
  weekdays?: any
  hours?: any
  next_run_at: string | null
  last_run_at?: string | null
  last_run_status?: RunStatus
  json_parameters?: any
}

interface ProcessRunLog {
  id: string
  schedule_id: string
  attempt_number: number
  started_at: string
  finished_at: string | null
  status: RunStatus
  error_message: string | null
  output: any
  worker_id: string | null
}

// 0=domingo .. 6=sábado
const WEEKDAYS = [
  { label: 'Dom', value: 0 },
  { label: 'Seg', value: 1 },
  { label: 'Ter', value: 2 },
  { label: 'Qua', value: 3 },
  { label: 'Qui', value: 4 },
  { label: 'Sex', value: 5 },
  { label: 'Sáb', value: 6 },
]

const DEFAULT_JSON_PARAMETERS_TEXT = JSON.stringify({
  parameters: [
    {
      parametro: '',
      valor: ''
    }
  ]
}, null, 2)

const ConfiguracoesAgendamentos: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'processos' | 'agendamentos' | 'log'>('processos')

  const [processes, setProcesses] = useState<Process[]>([])
  const [schedules, setSchedules] = useState<ProcessSchedule[]>([])
  const [logs, setLogs] = useState<ProcessRunLog[]>([])
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(false)
  const [logStatus, setLogStatus] = useState<'all' | 'success' | 'failed' | 'cancelled'>('all')
  const [logDateStart, setLogDateStart] = useState<string>('')
  const [logDateEnd, setLogDateEnd] = useState<string>('')
  const [logTimeStart, setLogTimeStart] = useState<string>('')
  const [logTimeEnd, setLogTimeEnd] = useState<string>('')
  const [logProcessKey, setLogProcessKey] = useState<string>('all')
  const [selectedLog, setSelectedLog] = useState<ProcessRunLog | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
  const [editingSchedule, setEditingSchedule] = useState<ProcessSchedule | null>(null)
  const [scheduleStatusFilter, setScheduleStatusFilter] = useState<'all' | 'enabled' | 'disabled'>('all')
  const [scheduleProcessFilter, setScheduleProcessFilter] = useState<string>('all')
  const [scheduleProcessKeyFilter, setScheduleProcessKeyFilter] = useState<string>('all')
  const [formValues, setFormValues] = useState({
    process_id: '',
    description: '',
    weekdays: [] as number[],
    hours: [] as number[],
    enabled: true,
    json_parameters_text: DEFAULT_JSON_PARAMETERS_TEXT
  })
  const [isScheduleSubmitting, setIsScheduleSubmitting] = useState<boolean>(false)

  // Formulário de Processos
  const [isProcessFormOpen, setIsProcessFormOpen] = useState<boolean>(false)
  const [editingProcess, setEditingProcess] = useState<Process | null>(null)
  const [processFormValues, setProcessFormValues] = useState({
    key: '',
    name: '',
    description: '',
    is_active: true,
  })
  const [isProcessSubmitting, setIsProcessSubmitting] = useState<boolean>(false)

  // Paginação
  const [currentPageProc, setCurrentPageProc] = useState<number>(1)
  const itemsPerPageProc = 20
  const [currentPageSch, setCurrentPageSch] = useState<number>(1)
  const itemsPerPageSch = 20
  const [currentPageLog, setCurrentPageLog] = useState<number>(1)
  const itemsPerPageLog = 20

  // Indicadores topo
  const [logFailedCount, setLogFailedCount] = useState<number | null>(null)
  const [logSuccessCount, setLogSuccessCount] = useState<number | null>(null)

  // Calendário (visualização dos agendamentos)
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false)

  const toggleWeekday = (day: number) => {
    setFormValues(prev => {
      const set = new Set(prev.weekdays)
      if (set.has(day)) set.delete(day); else set.add(day)
      return { ...prev, weekdays: Array.from(set).sort((a,b)=>a-b) }
    })
  }

  const parseNumericArrayText = (text?: string | null): number[] => {
    if (!text || typeof text !== 'string') return []
    const matches = text.match(/\d+/g)
    return matches ? matches.map((n) => Number(n)) : []
  }
  const toggleHour = (hour: number) => {
    setFormValues(prev => {
      const set = new Set(prev.hours)
      if (set.has(hour)) set.delete(hour); else set.add(hour)
      return { ...prev, hours: Array.from(set).sort((a,b)=>a-b) }
    })
  }

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [{ data: proc, error: pErr }, { data: sch, error: sErr }] = await Promise.all([
        supabase.from('processes').select('*').order('name', { ascending: true }),
        supabase.from('process_schedules').select('*').order('created_at', { ascending: false })
      ])
      if (pErr) throw pErr
      if (sErr) throw sErr
      setProcesses((proc || []) as any)
      setSchedules((sch || []) as any)
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  const loadLogs = async () => {
    try {
      setIsLoadingLogs(true)
      let q: any = supabase
        .from('process_run_logs')
        .select('*')

      if (logStatus !== 'all') {
        q = q.eq('status', logStatus)
      }
      if (logDateStart) {
        const [y, m, d] = logDateStart.split('-').map(Number)
        const [hs, ms] = (logTimeStart || '00:00').split(':').map(Number)
        const startIso = new Date(y, m - 1, d, hs || 0, ms || 0, 0, 0).toISOString()
        q = q.gte('started_at', startIso)
      }
      if (logDateEnd) {
        const [y, m, d] = logDateEnd.split('-').map(Number)
        const [he, me] = (logTimeEnd || '23:59').split(':').map(Number)
        const endIso = new Date(y, m - 1, d, he || 23, me || 59, 59, 999).toISOString()
        q = q.lte('started_at', endIso)
      }

      const { data, error: lErr } = await q
        .order('started_at', { ascending: false })
        .limit(100)
      if (lErr) throw lErr
      setLogs((data || []) as any)
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar logs')
    } finally {
      setIsLoadingLogs(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (activeTab === 'log') {
      loadLogs()
    }
  }, [activeTab, logStatus, logDateStart, logDateEnd, logTimeStart, logTimeEnd])

  // Recarregar contagem geral de logs
  useEffect(() => {
    const loadLogCounts = async () => {
      try {
        // Buscar as últimas 100 execuções ordenadas por started_at
        const { data: last100Logs, error: logsErr } = await supabase
          .from('process_run_logs')
          .select('status')
          .order('started_at', { ascending: false })
          .limit(100)
        
        if (logsErr) throw logsErr

        // Contar erros nas últimas 100 execuções
        const failedCount = (last100Logs || []).filter(log => log.status === 'failed').length
        
        // Contar sucessos nas últimas 100 execuções
        const successCount = (last100Logs || []).filter(log => log.status === 'success').length

        setLogFailedCount(failedCount)
        setLogSuccessCount(successCount)
      } catch (e) {
        // Mantém valores atuais em caso de erro
      }
    }
    loadLogCounts()
  }, [])

  const resetForm = () => {
    setEditingSchedule(null)
    setFormValues({
      process_id: '',
      description: '',
      weekdays: [],
      hours: [],
      enabled: true,
      json_parameters_text: DEFAULT_JSON_PARAMETERS_TEXT
    })
  }

  const openCreate = () => {
    resetForm()
    setIsFormOpen(true)
  }

  const openEdit = (schedule: ProcessSchedule) => {
    setEditingSchedule(schedule)
    setFormValues({
      process_id: schedule.process_id,
      description: schedule.description ?? '',
      weekdays: Array.isArray(schedule.weekdays) ? (schedule.weekdays as number[]) : (parseNumericArrayText(schedule.weekdays) || [1,2,3,4,5]),
      hours: Array.isArray(schedule.hours) ? (schedule.hours as number[]) : (parseNumericArrayText(schedule.hours) || [9]),
      enabled: schedule.enabled,
      json_parameters_text: (schedule as any).json_parameters ? (typeof (schedule as any).json_parameters === 'string' ? (schedule as any).json_parameters : JSON.stringify((schedule as any).json_parameters, null, 2)) : ''
    })
    setIsFormOpen(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as any
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const saveSchedule = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setError(null)
      const { data: authData, error: authErr } = await supabase.auth.getUser()
      if (authErr) throw authErr
      const currentUserId = authData?.user?.id || null
      if (!formValues.process_id) {
        setError('Selecione um processo')
        return
      }
      if (!formValues.weekdays || formValues.weekdays.length === 0) {
        setError('Selecione ao menos um dia da semana')
        return
      }
      if (!formValues.hours || formValues.hours.length === 0) {
        setError('Selecione ao menos uma hora')
        return
      }

      const weekdaysText = `[${formValues.weekdays.sort((a,b)=>a-b).join(',')}]`
      const hoursText = `[${formValues.hours.sort((a,b)=>a-b).map(h => h.toString().padStart(2,'0')).join(',')}]`

      let jsonParams: any = null
      if (formValues.json_parameters_text && formValues.json_parameters_text.trim() !== '') {
        try {
          jsonParams = JSON.parse(formValues.json_parameters_text)
        } catch {
          setError('Parâmetros (JSON) inválidos')
          return
        }
      }

      const base: any = {
        process_id: formValues.process_id,
        description: formValues.description || null,
        weekdays: weekdaysText,
        hours: hoursText,
        enabled: formValues.enabled,
        json_parameters: jsonParams
      }

      if (editingSchedule) {
        const { error: uErr } = await supabase
          .from('process_schedules')
          .update({ ...base, updated_by: currentUserId || undefined })
          .eq('id', editingSchedule.id)
        if (uErr) throw uErr
      } else {
        const { error: iErr } = await supabase
          .from('process_schedules')
          .insert({ ...base, created_by: currentUserId || undefined, updated_by: currentUserId || undefined })
        if (iErr) throw iErr
      }

      setIsFormOpen(false)
      resetForm()
      await loadData()
    } catch (e: any) {
      setError(e?.message || 'Erro ao salvar agendamento')
    }
  }

  const toggleEnabled = async (schedule: ProcessSchedule) => {
    try {
      const { data: authData } = await supabase.auth.getUser()
      const currentUserId = authData?.user?.id || null
      const { error: uErr } = await supabase
        .from('process_schedules')
        .update({ enabled: !schedule.enabled, updated_by: currentUserId || undefined })
        .eq('id', schedule.id)
      if (uErr) throw uErr
      await loadData()
    } catch (e: any) {
      setError(e?.message || 'Erro ao atualizar status')
    }
  }

  const removeSchedule = async (schedule: ProcessSchedule) => {
    try {
      if (!window.confirm('Tem certeza que deseja excluir este agendamento? Esta ação não pode ser desfeita.')) return
      const { error: dErr } = await supabase
        .from('process_schedules')
        .delete()
        .eq('id', schedule.id)
      if (dErr) throw dErr
      await loadData()
    } catch (e: any) {
      setError(e?.message || 'Erro ao excluir agendamento')
    }
  }

  const processById = useMemo(() => {
    const map = new Map<string, Process>()
    processes.forEach(p => map.set(p.id, p))
    return map
  }, [processes])

  const scheduleById = useMemo(() => {
    const map = new Map<string, ProcessSchedule>()
    schedules.forEach(s => map.set(s.id, s))
    return map
  }, [schedules])

  const filteredSchedules = useMemo(() => {
    return schedules.filter(s => {
      if (scheduleStatusFilter === 'enabled' && !s.enabled) return false
      if (scheduleStatusFilter === 'disabled' && s.enabled) return false
      if (scheduleProcessFilter !== 'all' && s.process_id !== scheduleProcessFilter) return false
      if (scheduleProcessKeyFilter !== 'all') {
        const proc = processById.get(s.process_id)
        if (proc?.key !== scheduleProcessKeyFilter) return false
      }
      return true
    })
  }, [schedules, scheduleStatusFilter, scheduleProcessFilter, scheduleProcessKeyFilter])

  const filteredLogs = useMemo(() => {
    if (logProcessKey === 'all') return logs
    // Filtra por chave do processo usando schedule_id -> process_id -> process.key
    return logs.filter(l => {
      const sch = scheduleById.get(l.schedule_id)
      if (!sch) return false
      const proc = processById.get(sch.process_id)
      return proc?.key === logProcessKey
    })
  }, [logs, logProcessKey, scheduleById, processById])

  // Execuções previstas hoje (soma das horas dos agendamentos elegíveis)
  const todayExecutionsCount = useMemo(() => {
    const today = new Date().getDay() // 0..6 (Dom..Sáb)
    let total = 0
    for (const s of schedules) {
      if (!s.enabled) continue
      const weekdays = Array.isArray(s.weekdays) ? (s.weekdays as number[]) : parseNumericArrayText(s.weekdays)
      if (!weekdays.includes(today)) continue
      const hoursArr = Array.isArray(s.hours) ? (s.hours as number[]) : parseNumericArrayText(s.hours)
      total += hoursArr.length
    }
    return total
  }, [schedules])

  // Paginação: Processos
  useEffect(() => { setCurrentPageProc(1) }, [processes])
  const totalPagesProc = Math.max(1, Math.ceil(processes.length / itemsPerPageProc))
  const procStart = (currentPageProc - 1) * itemsPerPageProc
  const procEnd = procStart + itemsPerPageProc
  const pagedProcesses = useMemo(() => processes.slice(procStart, procEnd), [processes, procStart, procEnd])

  // Paginação: Agendamentos
  useEffect(() => { setCurrentPageSch(1) }, [filteredSchedules])
  const totalPagesSch = Math.max(1, Math.ceil(filteredSchedules.length / itemsPerPageSch))
  const schStart = (currentPageSch - 1) * itemsPerPageSch
  const schEnd = schStart + itemsPerPageSch
  const pagedSchedules = useMemo(() => filteredSchedules.slice(schStart, schEnd), [filteredSchedules, schStart, schEnd])

  // Paginação: Logs
  useEffect(() => { setCurrentPageLog(1) }, [filteredLogs])
  const totalPagesLog = Math.max(1, Math.ceil(filteredLogs.length / itemsPerPageLog))
  const logStart = (currentPageLog - 1) * itemsPerPageLog
  const logEnd = logStart + itemsPerPageLog
  const pagedLogs = useMemo(() => filteredLogs.slice(logStart, logEnd), [filteredLogs, logStart, logEnd])

  const formatDuration = (startIso: string, endIso: string | null) => {
    if (!endIso) return '—'
    const start = new Date(startIso).getTime()
    const end = new Date(endIso).getTime()
    const diff = end - start
    if (isNaN(diff) || diff < 0) return '—'
    const totalSeconds = Math.floor(diff / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const hh = hours.toString().padStart(2, '0')
    const mm = minutes.toString().padStart(2, '0')
    const ss = seconds.toString().padStart(2, '0')
    return `${hh}:${mm}:${ss}`
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <main className="flex-1 flex flex-col">
          <PermissionGuard 
            moduleName="configuracoes_agendamentos" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                  <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
                </div>
              </div>
            }
          >
            <div className="h-full flex flex-col">
              {/* Header (padrão AtendimentoAnalise) */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">Agendamentos</Heading>
                    <Text size="sm" color="muted" className="mt-2">
                      Gerencie agendamentos de execução de processos e visualize o histórico de execuções.
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsCalendarOpen(true)}
                    >
                      Calendário
                    </Button>
                    {activeTab === 'agendamentos' && (
                      <Button
                        onClick={openCreate}
                        className="flex items-center gap-2 text-white"
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        <PlusIcon className="h-5 w-5" />
                        Novo Agendamento
                      </Button>
                    )}
                    {activeTab === 'processos' && (
                      <Button
                        onClick={() => { setEditingProcess(null); setProcessFormValues({ key: '', name: '', description: '', is_active: true }); setIsProcessFormOpen(true) }}
                        className="flex items-center gap-2 text-white"
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        <PlusIcon className="h-5 w-5" />
                        Novo Processo
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Indicadores topo */}
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{todayExecutionsCount}</Text>
                      <Text size="sm" className="text-white">Execuções previstas hoje</Text>
                    </div>
                  </div>
                  <div className={`bg-gradient-to-br shadow-lg rounded-lg p-6 text-white ${
                    logFailedCount !== null && logFailedCount === 0
                      ? 'from-green-500 to-green-600'
                      : 'from-red-500 to-red-600'
                  }`}>
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{logFailedCount ?? '—'}</Text>
                      <Text size="sm" className="text-white">Execuções com erro (últimas 100)</Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-600 to-green-700 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{logSuccessCount ?? '—'}</Text>
                      <Text size="sm" className="text-white">Execuções com sucesso (últimas 100)</Text>
                    </div>
                  </div>
                </div>
              </div>

              {/* Abas */}
              <div className="px-6 py-4">
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="-mb-px flex space-x-4" aria-label="Tabs">
                    <button onClick={() => setActiveTab('processos')} className={`whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm ${activeTab === 'processos' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'}`}>Processos</button>
                    <button onClick={() => setActiveTab('agendamentos')} className={`whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm ${activeTab === 'agendamentos' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'}`}>Agendamentos</button>
                    <button onClick={() => setActiveTab('log')} className={`whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm ${activeTab === 'log' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'}`}>Log</button>
                  </nav>
                </div>
              </div>
              {activeTab === 'processos' && (
                <div className="space-y-4">
                  <div className="px-6 text-sm text-gray-500 dark:text-gray-400">{processes.length} processos</div>

                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="p-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Chave</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Nome</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Descrição</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ativo</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {pagedProcesses.map(p => (
                          <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-gray-100">{p.key}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{p.name}</td>
                            <td className="px-4 py-3 whitespace-normal text-sm text-gray-700 dark:text-gray-300">{p.description || '—'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">{p.is_active ? <span className="text-green-600">Ativo</span> : <span className="text-gray-500">Inativo</span>}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <div className="flex justify-end items-center gap-2">
                                <Tooltip content="Editar">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setEditingProcess(p); setProcessFormValues({ key: p.key, name: p.name, description: p.description || '', is_active: p.is_active }); setIsProcessFormOpen(true) }}
                                    className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </Button>
                                </Tooltip>
                                <Tooltip content={p.is_active ? 'Desativar' : 'Ativar'}>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => { await supabase.from('processes').update({ is_active: !p.is_active }).eq('id', p.id); const { data, error: pErr } = await supabase.from('processes').select('*').order('name', { ascending: true }); if (!pErr) setProcesses((data || []) as any) }}
                                    className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                                  >
                                    <PowerIcon className="h-4 w-4" />
                                  </Button>
                                </Tooltip>
                                <Tooltip content="Excluir">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => { if (!window.confirm('Tem certeza que deseja excluir este processo? Esta ação não pode ser desfeita.')) return; await supabase.from('processes').delete().eq('id', p.id); const { data, error: pErr } = await supabase.from('processes').select('*').order('name', { ascending: true }); if (!pErr) setProcesses((data || []) as any) }}
                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </Button>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                      {totalPagesProc > 1 && (
                        <div className="px-2 py-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                              <Text size="sm">Mostrando {procStart + 1} a {Math.min(procEnd, processes.length)} de {processes.length} processos</Text>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button onClick={() => setCurrentPageProc(p => Math.max(1, p - 1))} disabled={currentPageProc === 1} className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">Anterior</button>
                              <div className="flex space-x-1">
                                {Array.from({ length: Math.min(5, totalPagesProc) }, (_, i) => {
                                  let pageNumber: number
                                  if (totalPagesProc <= 5) pageNumber = i + 1
                                  else if (currentPageProc <= 3) pageNumber = i + 1
                                  else if (currentPageProc >= totalPagesProc - 2) pageNumber = totalPagesProc - 4 + i
                                  else pageNumber = currentPageProc - 2 + i
                                  return (
                                    <button key={pageNumber} onClick={() => setCurrentPageProc(pageNumber)} className={`px-3 py-2 text-sm font-medium rounded-md ${currentPageProc === pageNumber ? 'bg-blue-600 text-white' : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'}`}>{pageNumber}</button>
                                  )
                                })}
                              </div>
                              <button onClick={() => setCurrentPageProc(p => Math.min(totalPagesProc, p + 1))} disabled={currentPageProc === totalPagesProc} className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">Próximo</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {isProcessFormOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsProcessFormOpen(false)}>
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                          <Heading as="h3" size="2xl">{editingProcess ? 'Editar processo' : 'Novo processo'}</Heading>
                          <Button onClick={() => setIsProcessFormOpen(false)} variant="ghost">
                            <XMarkIcon className="h-6 w-6" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Chave (slug)</label>
                            <Input
                              value={processFormValues.key}
                              onChange={e => setProcessFormValues(prev => ({ ...prev, key: e.target.value }))}
                              placeholder="ex: faturamento_diario"
                              className="font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nome</label>
                            <Input
                              value={processFormValues.name}
                              onChange={e => setProcessFormValues(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="Nome legível do processo"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descrição</label>
                            <Input
                              value={processFormValues.description}
                              onChange={e => setProcessFormValues(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Descreva o que o processo faz"
                            />
                          </div>
                          <div className="flex items-center gap-2 md:col-span-2">
                            <input id="proc_active" type="checkbox" checked={processFormValues.is_active} onChange={(e) => setProcessFormValues(prev => ({ ...prev, is_active: e.target.checked }))} />
                            <label htmlFor="proc_active" className="text-sm text-gray-700 dark:text-gray-300">Processo ativo</label>
                          </div>
                        </div>
                        {error && <div className="text-red-600 dark:text-red-400 mt-4">{error}</div>}
                        <div className="mt-6 flex justify-end gap-3">
                          <Button variant="ghost" onClick={() => setIsProcessFormOpen(false)}>Cancelar</Button>
                          <Button
                            onClick={async () => {
                              setIsProcessSubmitting(true)
                              try {
                                setError(null)
                                if (!processFormValues.key || !processFormValues.name) {
                                  setError('Preencha chave e nome do processo')
                                  setIsProcessSubmitting(false)
                                  return
                                }
                                if (editingProcess) {
                                  const { error: uErr } = await supabase.from('processes').update({
                                    key: processFormValues.key,
                                    name: processFormValues.name,
                                    description: processFormValues.description || null,
                                    is_active: processFormValues.is_active
                                  }).eq('id', editingProcess.id)
                                  if (uErr) throw uErr
                                } else {
                                  const { error: iErr } = await supabase.from('processes').insert({
                                    key: processFormValues.key,
                                    name: processFormValues.name,
                                    description: processFormValues.description || null,
                                    is_active: processFormValues.is_active
                                  })
                                  if (iErr) throw iErr
                                }
                                setIsProcessFormOpen(false)
                                setEditingProcess(null)
                                setProcessFormValues({ key: '', name: '', description: '', is_active: true })
                                const { data, error: pErr } = await supabase.from('processes').select('*').order('name', { ascending: true })
                                if (!pErr) setProcesses((data || []) as any)
                              } catch (e: any) {
                                setError(e?.message || 'Erro ao salvar processo')
                              } finally {
                                setIsProcessSubmitting(false)
                              }
                            }}
                            className="text-white"
                            style={{ backgroundColor: BRAND_COLOR }}
                            disabled={isProcessSubmitting}
                          >
                            {isProcessSubmitting ? 'Salvando...' : 'Salvar Processo'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="px-6 mb-4 p-3 rounded bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">{error}</div>
              )}

              {activeTab === 'agendamentos' && (
                <div className="space-y-4">
                  <div className="px-6 flex flex-col gap-3">
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</label>
                        <Select value={scheduleStatusFilter} onChange={e => setScheduleStatusFilter((e.target as any).value)}>
                          <option value="all">Todos</option>
                          <option value="enabled">Ativo</option>
                          <option value="disabled">Pausado</option>
                        </Select>
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Processo</label>
                        <Select value={scheduleProcessFilter} onChange={e => setScheduleProcessFilter((e.target as any).value)}>
                          <option value="all">Todos</option>
                          {processes.map(p => (
                            <option key={p.id} value={p.id}>{p.name} ({p.key})</option>
                          ))}
                        </Select>
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Chave</label>
                        <Select value={scheduleProcessKeyFilter} onChange={e => setScheduleProcessKeyFilter((e.target as any).value)}>
                          <option value="all">Todas</option>
                          {processes.map(p => (
                            <option key={p.id} value={p.key}>{p.key}</option>
                          ))}
                        </Select>
                      </div>
                      <div className="ml-auto flex gap-2">
                        <Button variant="outline" onClick={() => { setScheduleStatusFilter('all'); setScheduleProcessFilter('all'); setScheduleProcessKeyFilter('all'); }}>Limpar filtros</Button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{filteredSchedules.length} agendamentos</div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="p-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Processo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Descrição</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ativo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Última</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {pagedSchedules.map(s => (
                          <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{processById.get(s.process_id)?.name || processById.get(s.process_id)?.key || '—'}</td>
                            <td className="px-4 py-3 whitespace-normal text-sm text-gray-700 dark:text-gray-300">{s.description || '—'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">{s.enabled ? <span className="text-green-600">Ativo</span> : <span className="text-gray-500">Pausado</span>}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{s.last_run_at ? `${new Date(s.last_run_at).toLocaleString()}${s.last_run_status ? ` (${s.last_run_status})` : ''}` : '—'}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">
                              <div className="flex justify-end items-center gap-2">
                                <Tooltip content="Editar">
                                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300">
                                    <PencilIcon className="h-4 w-4" />
                                  </Button>
                                </Tooltip>
                                <Tooltip content={s.enabled ? 'Pausar' : 'Ativar'}>
                                  <Button size="sm" variant="ghost" onClick={() => toggleEnabled(s)} className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300">
                                    <PowerIcon className="h-4 w-4" />
                                  </Button>
                                </Tooltip>
                                <Tooltip content="Excluir">
                                  <Button size="sm" variant="ghost" onClick={() => removeSchedule(s)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300">
                                    <TrashIcon className="h-4 w-4" />
                                  </Button>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                      {totalPagesSch > 1 && (
                        <div className="px-2 py-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                              <Text size="sm">Mostrando {schStart + 1} a {Math.min(schEnd, filteredSchedules.length)} de {filteredSchedules.length} agendamentos</Text>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button onClick={() => setCurrentPageSch(p => Math.max(1, p - 1))} disabled={currentPageSch === 1} className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">Anterior</button>
                              <div className="flex space-x-1">
                                {Array.from({ length: Math.min(5, totalPagesSch) }, (_, i) => {
                                  let pageNumber: number
                                  if (totalPagesSch <= 5) pageNumber = i + 1
                                  else if (currentPageSch <= 3) pageNumber = i + 1
                                  else if (currentPageSch >= totalPagesSch - 2) pageNumber = totalPagesSch - 4 + i
                                  else pageNumber = currentPageSch - 2 + i
                                  return (
                                    <button key={pageNumber} onClick={() => setCurrentPageSch(pageNumber)} className={`px-3 py-2 text-sm font-medium rounded-md ${currentPageSch === pageNumber ? 'bg-blue-600 text-white' : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'}`}>{pageNumber}</button>
                                  )
                                })}
                              </div>
                              <button onClick={() => setCurrentPageSch(p => Math.min(totalPagesSch, p + 1))} disabled={currentPageSch === totalPagesSch} className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">Próximo</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {isFormOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsFormOpen(false)}>
                      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                          <Heading as="h3" size="2xl">{editingSchedule ? 'Editar agendamento' : 'Novo agendamento'}</Heading>
                          <Button onClick={() => setIsFormOpen(false)} variant="ghost">
                            <XMarkIcon className="h-6 w-6" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Processo</label>
                            <Select value={formValues.process_id} onChange={e => setFormValues(prev => ({ ...prev, process_id: (e.target as any).value }))}>
                              <option value="">Selecione...</option>
                              {processes.map(p => (
                                <option key={p.id} value={p.id}>{p.name} ({p.key})</option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descrição</label>
                            <Input name="description" value={formValues.description} onChange={e => setFormValues(prev => ({ ...prev, description: (e.target as any).value }))} placeholder="Descrição do agendamento" />
                          </div>
                          <div className="md:col-span-2">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">json_parameters</label>
                              <div className="flex gap-3">
                                <Button
                                  size="sm"
                                  variant="link"
                                  onClick={() => setFormValues(prev => ({ ...prev, json_parameters_text: `{"parameters":[{"parametro":"data","valor":"$$dataatual"}]}` }))}
                                >
                                  Data Atual
                                </Button>
                                <Button
                                  size="sm"
                                  variant="link"
                                  onClick={() => setFormValues(prev => ({ ...prev, json_parameters_text: `{"parameters":[{"parametro":"datainicio","valor":"00/00/0000"}, {"parametro":"datafim","valor":"00/00/0000"}]}` }))}
                                >
                                  Período
                                </Button>
                                <Button
                                  size="sm"
                                  variant="link"
                                  onClick={() => setFormValues(prev => ({ ...prev, json_parameters_text: DEFAULT_JSON_PARAMETERS_TEXT }))}
                                >
                                  Resetar
                                </Button>
                              </div>
                            </div>
                            <textarea
                              name="json_parameters_text"
                              value={(formValues as any).json_parameters_text}
                              onChange={handleChange}
                              rows={8}
                              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 text-sm text-gray-900 dark:text-gray-100 font-mono"
                              placeholder="{ }"
                            />
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Atenção: atualizações com períodos longos podem causar travamentos.</p>
                          </div>
                          <div className="md:col-span-2">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Dias da semana</label>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setFormValues(prev => ({ ...prev, weekdays: WEEKDAYS.map(d => d.value) }))}>Selecionar todos</Button>
                                <Button size="sm" variant="ghost" onClick={() => setFormValues(prev => ({ ...prev, weekdays: [] }))}>Limpar</Button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {WEEKDAYS.map(d => (
                                <label key={d.value} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer select-none">
                                  <input type="checkbox" checked={formValues.weekdays.includes(d.value)} onChange={() => toggleWeekday(d.value)} />
                                  <span className="text-sm">{d.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Horas do dia</label>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setFormValues(prev => ({ ...prev, hours: Array.from({length:24},(_,i)=>i) }))}>Selecionar todos</Button>
                                <Button size="sm" variant="ghost" onClick={() => setFormValues(prev => ({ ...prev, hours: [] }))}>Limpar</Button>
                              </div>
                            </div>
                            <div className="grid grid-cols-6 gap-2">
                              {Array.from({ length: 24 }, (_, i) => i).map(h => (
                                <label key={h} className={`inline-flex items-center justify-center gap-2 px-2 py-1.5 rounded-md border text-sm ${formValues.hours.includes(h) ? 'border-blue-500 text-blue-700 dark:text-blue-300' : 'border-gray-300 dark:border-gray-600'}`}>
                                  <input type="checkbox" checked={formValues.hours.includes(h)} onChange={() => toggleHour(h)} />
                                  {h.toString().padStart(2,'0')}h
                                </label>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 md:col-span-2">
                            <input id="enabled" type="checkbox" checked={formValues.enabled} onChange={(e) => setFormValues(prev => ({ ...prev, enabled: (e.target as any).checked }))} />
                            <label htmlFor="enabled" className="text-sm text-gray-700 dark:text-gray-300">Ativo</label>
                          </div>
                        </div>
                        {/* Campos de API removidos conforme solicitação */}
                        {error && <div className="text-red-600 dark:text-red-400 mt-4">{error}</div>}
                        <div className="mt-6 flex justify-end gap-3">
                          <Button variant="ghost" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
                          <Button
                            onClick={async () => {
                              setIsScheduleSubmitting(true)
                              try {
                                await saveSchedule(new Event('submit') as any)
                              } finally {
                                setIsScheduleSubmitting(false)
                              }
                            }}
                            className="text-white"
                            style={{ backgroundColor: BRAND_COLOR }}
                            disabled={isScheduleSubmitting}
                          >
                            {isScheduleSubmitting ? 'Salvando...' : 'Salvar Agendamento'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'log' && (
                <div className="space-y-4">
                  <div className="px-6 flex flex-col gap-3">
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Status</label>
                        <Select value={logStatus} onChange={e => setLogStatus((e.target as any).value)}>
                          <option value="all">Todos</option>
                          <option value="success">success</option>
                          <option value="failed">failed</option>
                          <option value="cancelled">cancelled</option>
                        </Select>
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Chave</label>
                        <Select value={logProcessKey} onChange={e => setLogProcessKey((e.target as any).value)}>
                          <option value="all">Todas</option>
                          {processes.map(p => (
                            <option key={p.id} value={p.key}>{p.key}</option>
                          ))}
                        </Select>
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Data inicial</label>
                        <Input type="date" value={logDateStart} onChange={e => setLogDateStart((e.target as any).value)} />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Data final</label>
                        <Input type="date" value={logDateEnd} onChange={e => setLogDateEnd((e.target as any).value)} />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Hora inicial</label>
                        <Input type="time" value={logTimeStart} onChange={e => setLogTimeStart((e.target as any).value)} />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1">Hora final</label>
                        <Input type="time" value={logTimeEnd} onChange={e => setLogTimeEnd((e.target as any).value)} />
                      </div>
                      <div className="ml-auto flex gap-2">
                        <Button
                          onClick={() => loadLogs()}
                          className="text-white"
                          style={{ backgroundColor: BRAND_COLOR }}
                          disabled={isLoadingLogs}
                        >
                          {isLoadingLogs ? 'Atualizando...' : 'Atualizar'}
                        </Button>
                        <Button variant="outline" onClick={() => { setLogStatus('all'); setLogProcessKey('all'); setLogDateStart(''); setLogDateEnd(''); setLogTimeStart(''); setLogTimeEnd(''); }}>Limpar filtros</Button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{isLoadingLogs ? 'Carregando...' : `Últimas ${filteredLogs.length} execuções`}</div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="p-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Chave</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Proc. Nome</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Início</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Output</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tempo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {pagedLogs.map(l => (
                          <tr
                            key={l.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                            onClick={() => setSelectedLog(l)}
                          >
                            {(() => { const sch = scheduleById.get(l.schedule_id); const proc = sch ? processById.get(sch.process_id) : undefined; return (
                              <>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900 dark:text-gray-100">{proc?.key || '—'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{proc?.name || '—'}</td>
                              </>
                            ) })()}
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{new Date(l.started_at).toLocaleString()}</td>
                            <td className="px-4 py-3 whitespace-normal text-xs text-gray-700 dark:text-gray-300 font-mono">
                              {(() => { try { const s = JSON.stringify(l.output); return s.length > 120 ? s.slice(0,120) + '…' : s } catch { return '—' } })()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{formatDuration(l.started_at, l.finished_at)}</td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm">{l.status ? (
                              l.status === 'success' ? <span className="text-green-600">success</span> : l.status === 'failed' ? <span className="text-red-600">failed</span> : <span className="text-gray-600">cancelled</span>
                            ) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                      {totalPagesLog > 1 && (
                        <div className="px-2 py-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                              <Text size="sm">Mostrando {logStart + 1} a {Math.min(logEnd, filteredLogs.length)} de {filteredLogs.length} execuções</Text>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button onClick={() => setCurrentPageLog(p => Math.max(1, p - 1))} disabled={currentPageLog === 1} className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">Anterior</button>
                              <div className="flex space-x-1">
                                {Array.from({ length: Math.min(5, totalPagesLog) }, (_, i) => {
                                  let pageNumber: number
                                  if (totalPagesLog <= 5) pageNumber = i + 1
                                  else if (currentPageLog <= 3) pageNumber = i + 1
                                  else if (currentPageLog >= totalPagesLog - 2) pageNumber = totalPagesLog - 4 + i
                                  else pageNumber = currentPageLog - 2 + i
                                  return (
                                    <button key={pageNumber} onClick={() => setCurrentPageLog(pageNumber)} className={`px-3 py-2 text-sm font-medium rounded-md ${currentPageLog === pageNumber ? 'bg-blue-600 text-white' : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'}`}>{pageNumber}</button>
                                  )
                                })}
                              </div>
                              <button onClick={() => setCurrentPageLog(p => Math.min(totalPagesLog, p + 1))} disabled={currentPageLog === totalPagesLog} className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">Próximo</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedLog && (() => {
                    const sch = scheduleById.get(selectedLog.schedule_id)
                    const proc = sch ? processById.get(sch.process_id) : undefined
                    return (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedLog(null)}>
                        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-between mb-4">
                            <Heading as="h3" size="2xl">Detalhes da execução</Heading>
                            <Button variant="ghost" onClick={() => setSelectedLog(null)}><XMarkIcon className="h-6 w-6" /></Button>
                          </div>
                          <div className="space-y-6 text-sm">
                            <div>
                              <Heading as="h4" size="2xl" className="mb-2">Log</Heading>
                              <div className="grid grid-cols-2 gap-3">
                                <div><span className="text-gray-500">ID:</span> <span className="font-mono">{selectedLog.id}</span></div>
                                <div><span className="text-gray-500">Schedule ID:</span> <span className="font-mono">{selectedLog.schedule_id}</span></div>
                                <div><span className="text-gray-500">Início:</span> <span>{new Date(selectedLog.started_at).toLocaleString()}</span></div>
                                <div><span className="text-gray-500">Fim:</span> <span>{selectedLog.finished_at ? new Date(selectedLog.finished_at).toLocaleString() : '—'}</span></div>
                                <div><span className="text-gray-500">Status:</span> <span>{selectedLog.status || '—'}</span></div>
                                <div><span className="text-gray-500">Tentativa:</span> <span>{selectedLog.attempt_number}</span></div>
                                <div className="col-span-2"><span className="text-gray-500">Erro:</span> <span className="break-words">{selectedLog.error_message || '—'}</span></div>
                                <div className="col-span-2">
                                  <span className="text-gray-500">Output:</span>
                                  <pre className="mt-1 p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs overflow-auto max-h-64">
{JSON.stringify(selectedLog.output, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                            <div>
                              <Heading as="h4" size="2xl" className="mb-2">Processo</Heading>
                              <div className="grid grid-cols-2 gap-3">
                                <div><span className="text-gray-500">Key:</span> <span className="font-mono">{proc?.key || '—'}</span></div>
                                <div><span className="text-gray-500">Nome:</span> <span>{proc?.name || '—'}</span></div>
                                <div className="col-span-2"><span className="text-gray-500">Descrição:</span> <span>{proc?.description || '—'}</span></div>
                                <div><span className="text-gray-500">Ativo:</span> <span>{proc?.is_active ? 'Sim' : 'Não'}</span></div>
                              </div>
                            </div>
                            <div>
                              <Heading as="h4" size="2xl" className="mb-2">Agendamento</Heading>
                              <div className="grid grid-cols-2 gap-3">
                                <div><span className="text-gray-500">ID:</span> <span className="font-mono">{sch?.id || '—'}</span></div>
                                <div><span className="text-gray-500">Processo ID:</span> <span className="font-mono">{sch?.process_id || '—'}</span></div>
                                <div className="col-span-2"><span className="text-gray-500">Descrição:</span> <span>{sch?.description || '—'}</span></div>
                                <div><span className="text-gray-500">Ativo:</span> <span>{sch?.enabled ? 'Sim' : 'Não'}</span></div>
                                <div className="col-span-2"><span className="text-gray-500">Dias (raw):</span> <span className="font-mono">{(sch as any)?.weekdays ?? '—'}</span></div>
                                <div className="col-span-2"><span className="text-gray-500">Horas (raw):</span> <span className="font-mono">{(sch as any)?.hours ?? '—'}</span></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
              <CalendarModal
                open={isCalendarOpen}
                onOpenChange={setIsCalendarOpen}
                schedules={schedules}
                processes={processes}
              />
            </div>
          </PermissionGuard>
        </main>
      </div>
    </div>
  )
}

export default ConfiguracoesAgendamentos


